
import IconCheckmark from '@/components/svg/checkmark.svg?inline';
import IconExclamationMark from '@/components/svg/exclamation-mark.svg?inline';
import { SelectItem } from '@/models/SelectItem';
import scrollIntoView from '@/utils/scrollIntoView';
import { getItemByValue } from '@/utils/selectItemTools';
import {
  computed, defineComponent, nextTick, onMounted, PropType, ref, toRefs, watch
} from 'vue';
import {
  debouncedRef, onClickOutside, templateRef
} from '@vueuse/core';
import { clamp } from 'lodash';
import { AtBeforeInputValidation } from '@/models/BcxForm';
import useBeforeInput from '@/mixins/useBeforeInput';

interface ListItem extends SelectItem {
  lcText?: string; // lowercase cached
}

export default defineComponent({
  name: 'BCXSearchSelect',
  components: {
    IconCheckmark,
    IconExclamationMark
  },
  props: {
    // v-model value. Will always return the selected value.
    // except if "AllowAnything" is set, then it will return exactly what's been written there.
    value: {
      type: [Number, String, Boolean],
      default: ''
    },
    // "select" mode will behave like a select. REALLY.
    // "input" mode will behave like an input field combined with a select.
    mode: {
      type: String as PropType<'select' | 'input' | 'hybrid'>, // add 'hybrid'
      default: 'select'
    },
    // the list of items, in the format [{ value: string, text: string }, ...]
    list: {
      type: Array as PropType<ListItem[]>,
      default: () => []
    },
    // If set, you can type anything in the field and it will be returned on Enter. (mostly useful when value === text in the list)
    allowAnything: {
      type: Boolean
    },
    // Placeholder to show when nothing is selected
    placeholder: {
      type: String,
      default: ''
    },
    // Will clear the input field on click if this is set.
    clearOnClick: {
      type: Boolean
    },
    showValidityIndicator: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    selectedText: {
      type: String as () => string | null,
      default: null
    },
    invalid: {
      type: Boolean
    },
    label: {
      type: String,
      default: '',
    },
    maxlength: {
      type: [String, Number, Boolean],
      default: false
    },
    type: {
      type: String as PropType<'default' | 'in-panel'>,
      default: 'default'
    },
    atBeforeInput: {
      type: Array as PropType<AtBeforeInputValidation[]>,
      default: () => []
    }
  },
  emits: ['actively-select', 'search', 'update:search', 'update:selected-text', 'input'],
  setup(props, { emit }) {
    const {
      value, list, mode, allowAnything, clearOnClick, selectedText, disabled
    } = toRefs(props);
    const { handleBeforeInputFunctions } = useBeforeInput();

    const getItemFromValue = (value: string | number | boolean) => getItemByValue(list.value, value);

    const inputText = ref<string | number | boolean>('');
    const selectedDisplayText = ref<string | number | boolean | null>(null);
    const isInputFocussed = ref<boolean>(false); // Explicitly set to false on creation
    const listElement = templateRef<HTMLElement>('list');
    const el = templateRef<HTMLElement>('el');
    const cursorPosition = ref<number>(-1);
    const lcList = computed(() => (list.value?.length
      ? list.value.map((listItem) => ({
        ...listItem,
        lcText: listItem.text.toLowerCase()
      }))
      : []));
    const inputTextDebounced = debouncedRef(inputText, 250);
    const dropdownKey = ref(0);

    watch(inputTextDebounced, (text) => {
      emit('update:search', text);
    });

    const initialText = computed(() => {
      const itemFromValue = getItemFromValue(value.value);
      if (itemFromValue) {
        return itemFromValue?.selectedText ?? itemFromValue?.text;
      }
      return selectedText.value ?? value.value;
    });

    watch(initialText, (initialText) => {
      selectedDisplayText.value = initialText;
    });

    const isHybrid = computed(() => mode.value === 'hybrid');

    // Track if we have a selected option in hybrid mode
    const hasSelectedOption = ref(false);

    // In hybrid mode, we need separate handling for display value and actual value
    const displayValue = computed<string | number | boolean>({
      get(): string | number | boolean {
        if (isHybrid.value) {
          if (hasSelectedOption.value) {
            // Show the display text of the selected option
            const item = getItemFromValue(inputText.value);
            return item ? item.text : inputText.value;
          }
          // User is typing, show the typed text
          return inputText.value;
        }
        return inputText.value;
      },
      set(val: string | number | boolean) {
        if (isHybrid.value) {
          // When user types, clear the selected option flag
          hasSelectedOption.value = false;
          inputText.value = val;
        } else {
          inputText.value = val;
        }
      }
    });
    const selectItem = (value: string | boolean | number) => {
      const item = getItemFromValue(value);
      if (item) {
        selectedDisplayText.value = item.text;
        emit('update:selected-text', item.text);
        // In hybrid mode, mark that we have a selected option and store the actual value
        if (isHybrid.value) {
          hasSelectedOption.value = true;
          inputText.value = value;
        } else if (mode.value === 'input') {
          inputText.value = item.text;
        } else {
          // In select mode, inputText should hold the actual value
          inputText.value = value;
        }
      }
      emit('input', value);
      isInputFocussed.value = false;
    };

    watch(isInputFocussed, () => {
      cursorPosition.value = -1;
    });

    const lcInputText = computed(() => (inputText.value as string).toLowerCase());

    // Hybrid mode: allow both selecting and free input
    const shownList = computed(() => {
      if (isHybrid.value) return list.value; // Do not filter in hybrid mode
      if (!inputText.value || mode.value === 'select') return list.value;
      return lcList.value.filter(({ text }) => text.toLowerCase().includes(lcInputText.value));
    });
    const dropdownForceVisible = ref(false);
    const isDropdownVisible = computed(() => (isInputFocussed.value && shownList.value.length > 0 && !disabled.value));

    let prevListLength = list.value.length;
    watch(() => shownList.value.length, (newLength) => {
      if (isInputFocussed.value && prevListLength === 0 && newLength > 0) {
        dropdownForceVisible.value = false;
        nextTick(() => {
          dropdownForceVisible.value = true;
        });
      }
      prevListLength = newLength;
    });

    const selectAnythingHybrid = () => {
      emit('input', inputText.value);
      emit('update:selected-text', inputText.value);
      isInputFocussed.value = false;
      dropdownForceVisible.value = false;
    };

    const moveCursor = (amount: number) => {
      if (!isDropdownVisible.value) return;
      cursorPosition.value = clamp(cursorPosition.value + amount, 0, shownList.value.length - 1);
      nextTick(() => {
        const cursorElement = listElement.value?.querySelector('.c-list--cursor');
        if (cursorElement) scrollIntoView(cursorElement as HTMLElement, el.value as HTMLElement);
      });
    };
    const selectCurrentItem = () => {
      if (!isInputFocussed.value) return;
      if (cursorPosition.value < 0 && (allowAnything.value || isHybrid.value)) {
        selectAnythingHybrid();
      } else {
        if (!isDropdownVisible.value) return;
        const itemValue = shownList.value?.[cursorPosition.value]?.value;
        if (itemValue !== undefined) selectItem(itemValue);
      }
    };
    const onTab = () => {
      selectCurrentItem();
      isInputFocussed.value = false;
      cursorPosition.value = -1;
    };
    const onInputFocus = () => {
      if (!props.disabled) isInputFocussed.value = true;
    };
    const closeDropdown = () => {
      isInputFocussed.value = false;
      cursorPosition.value = -1;
    };
    const openDropdown = () => {
      if (!props.disabled) isInputFocussed.value = true;
    };
    const toggleDropdown = () => {
      if (isInputFocussed.value) closeDropdown();
      else openDropdown();
    };

    onClickOutside(el, () => {
      selectCurrentItem();
      closeDropdown();
    });

    const onInputMouseDown = () => {
      if (clearOnClick.value) inputText.value = '';
    };
    const isValid = computed(() => lcList.value.some((item) => item.lcText === lcInputText.value || item.selectedText === inputText.value));

    const classNames = computed(() => {
      const names = [];
      if (props.type !== 'default') names.push(`bc-search-select--${props.type}`);
      if (props.disabled) names.push('bc-search-select--disabled');
      return names;
    });

    function updateInputTextWithValue() {
      if (mode.value === 'select') {
        const item = getItemFromValue(value.value);
        if (item) {
          selectedDisplayText.value = item.text;
          inputText.value = value.value; // Set inputText to the actual value in select mode
        }
      } else if (mode.value === 'hybrid') {
        const item = getItemFromValue(value.value);
        if (item) {
          // If there's a matching item, mark it as selected and store the value
          hasSelectedOption.value = true;
          inputText.value = value.value;
          selectedDisplayText.value = item.text;
        } else {
          // If no matching item, treat as user input
          hasSelectedOption.value = false;
          inputText.value = value.value;
        }
      } else {
        inputText.value = initialText.value;
      }
    }

    onMounted(() => {
      updateInputTextWithValue();
      isInputFocussed.value = false; // Ensure dropdown is closed on mount
    });

    watch(value, () => {
      updateInputTextWithValue();
    });

    watch(() => list.value, (newVal, oldVal) => {
      if (oldVal.length === 0 && mode.value === 'select') {
        updateInputTextWithValue();
      }
    });
    return {
      isValid,
      isInputFocussed,
      inputText,
      isDropdownVisible,
      shownList,
      cursorPosition,
      selectItem,
      onInputMouseDown,
      toggleDropdown,
      classNames,
      selectedDisplayText,
      isHybrid,
      selectAnythingHybrid,
      dropdownKey,
      moveCursor,
      onTab,
      openDropdown,
      closeDropdown,
      onInputFocus,
      displayValue,
      hasSelectedOption,
      handleBeforeInputFunctions
    };
  }
});
